
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export function useCompanyData() {
  const [companyData, setCompanyData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    async function fetchCompanyData() {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        console.log("User metadata:", user.user_metadata);
        
        // Get company ID from metadata - check both possible naming conventions
        let companyId = user.user_metadata?.company_id || user.user_metadata?.companyId;
        
        // If no company ID, try to lookup by company name
        if (!companyId && user.user_metadata?.company_name) {
          const companyName = user.user_metadata.company_name;
          console.log("No company ID found, looking up by name:", companyName);
          
          const { data: companyByName, error: nameError } = await supabase
            .from('companies')
            .select('id')
            .ilike('name', companyName)
            .limit(1)
            .single();
            
          if (nameError) {
            console.error("Error looking up company by name:", nameError);
          } else if (companyByName) {
            companyId = companyByName.id;
            console.log("Found company by name, ID:", companyId);
          }
        }
        
        if (!companyId) {
          console.error("No company ID found in user metadata:", user.user_metadata);
          setLoading(false);
          toast({
            title: "Unable to find company ID",
            description: "Unable to find company ID in your account. Please contact support.",
            variant: "destructive"
          });
          return;
        }
        
        // Fetch company data from database
        const { data: company, error } = await supabase
          .from('companies')
          .select('*')
          .eq('id', companyId)
          .single();
        
        if (error) {
          console.error("Error fetching company:", error);
          toast({
            title: "Unable to load company information",
            description: error.message,
            variant: "destructive"
          });
        } else {
          console.log("Company data loaded:", company);
          setCompanyData(company);
        }
      } catch (err) {
        console.error("Error in fetchCompanyData:", err);
        toast({
          title: "Unable to load company information",
          description: "An unexpected error occurred. Please try again later.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    }
    
    fetchCompanyData();
  }, [user, toast]);

  return { companyData, loading, setCompanyData };
}
